import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { Enums } from "@how/database";
import { z } from "zod";
import { supabase } from "../../lib/supabase";

export type WebhookStatus = Enums<"webhook_event_status">;

/**
 * The ledger's statuses (§13.4), in the words the screen uses. A failed event is tried again on its own; a dead one
 * ran out of attempts, or was set aside on purpose, and waits for a person (ADR 0060).
 */
export const STATUS_LABEL: Record<WebhookStatus, string> = {
  received: "Received",
  processed: "Processed",
  ignored: "Ignored",
  failed: "Failed, retrying",
  dead: "Gave up",
};

/**
 * The status filter's choices. "attention" (gave up, failed and ignored) is the default, because those are the rows
 * someone has to look at; "any" asks for every status.
 */
export const STATUS_FILTERS = {
  attention: { label: "Gave up, failed and ignored", statuses: ["dead", "failed", "ignored"] },
  dead: { label: "Gave up", statuses: ["dead"] },
  failed: { label: "Failed, retrying", statuses: ["failed"] },
  ignored: { label: "Ignored", statuses: ["ignored"] },
  processed: { label: "Processed", statuses: ["processed"] },
  received: { label: "Received, not yet handled", statuses: ["received"] },
  any: { label: "Any status", statuses: null },
} as const satisfies Record<string, { label: string; statuses: readonly WebhookStatus[] | null }>;

export type StatusFilter = keyof typeof STATUS_FILTERS;

/** The filter in the address, or the default when it is missing or not one of the choices. */
export function statusFilter(value: string): StatusFilter {
  return Object.hasOwn(STATUS_FILTERS, value) ? (value as StatusFilter) : "attention";
}

const event = z.object({
  id: z.string(),
  receivedAt: z.string(),
  processedAt: z.string().nullable(),
  provider: z.string(),
  providerName: z.string(),
  eventType: z.string().nullable(),
  status: z.enum(["received", "processed", "ignored", "failed", "dead"]),
  error: z.string().nullable(),
  providerEventId: z.string(),
  tenantId: z.string().nullable(),
  church: z.string().nullable(),
  /** How many times HoW has tried it, and when it tries next (Stripe events; ADR 0060). */
  attempts: z.number().default(0),
  nextAttemptAt: z.string().nullable().default(null),
});
export type WebhookEvent = z.infer<typeof event>;

const page = z.object({ total: z.number(), events: z.array(event) });

export interface WebhookEventsQuery {
  /** A provider's event id; when given, it is the only filter. */
  readonly eventId: string | null;
  readonly provider: string | null;
  readonly status: StatusFilter;
  readonly pageIndex: number;
  readonly pageSize: number;
}

/**
 * The webhook ledger, newest first, filtered and paged by the database (`platform_webhook_events`, platform super
 * admins only). Never the payload: the function does not return it (D8).
 */
export function useWebhookEvents(q: WebhookEventsQuery) {
  return useQuery({
    queryKey: ["webhook-events", q],
    placeholderData: keepPreviousData,
    retry: false,
    queryFn: async (): Promise<{ rows: WebhookEvent[]; total: number }> => {
      const statuses = STATUS_FILTERS[q.status].statuses;
      const { data, error } = await supabase.rpc("platform_webhook_events", {
        ...(q.provider === null ? {} : { p_provider: q.provider }),
        // An explicit null asks for every status; leaving it out would mean the default, failed and ignored.
        p_statuses: statuses === null ? (null as unknown as WebhookStatus[]) : [...statuses],
        p_limit: q.pageSize,
        p_offset: q.pageIndex * q.pageSize,
        ...(q.eventId === null ? {} : { p_event_id: q.eventId }),
      });
      if (error) throw error;
      const parsed = page.parse(data);
      return { rows: parsed.events, total: parsed.total };
    },
  });
}

/** The providers a webhook can come from, for the filter. */
export function useProviders() {
  return useQuery({
    queryKey: ["integration-providers"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("integration_providers")
        .select("key, name")
        .order("name");
      if (error) throw error;
      return data;
    },
  });
}

/** How many webhooks failed in the last 24 hours, for the Overview. */
export function useWebhookFailures() {
  return useQuery({
    queryKey: ["webhook-failures"],
    retry: false,
    queryFn: async (): Promise<number> => {
      const { data, error } = await supabase.rpc("platform_webhook_failures");
      if (error) throw error;
      return data;
    },
  });
}

/** Whether HoW can retry an event itself: a Stripe event that failed or gave up (platform_retry_webhook_event). */
export function canRetry(event: Pick<WebhookEvent, "provider" | "status">): boolean {
  return event.provider === "stripe" && (event.status === "failed" || event.status === "dead");
}

/**
 * A platform super admin retries a failed or dead Stripe event: its attempts start again and the stripe-sweep worker
 * takes it within a minute, asking Stripe for the current state of what it names. Audited in the database.
 */
export function useRetryWebhookEvent() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (eventId: string): Promise<void> => {
      const { error } = await supabase.rpc("platform_retry_webhook_event", { p_event_id: eventId });
      if (error) throw error;
    },
    onSuccess: () =>
      Promise.all([
        client.invalidateQueries({ queryKey: ["webhook-events"] }),
        client.invalidateQueries({ queryKey: ["webhook-failures"] }),
      ]),
  });
}
